import { SITE_TUNING } from "./siteTuning";

// A separate, pointer-transparent plate preserves the board's layout and hover targets.
export class VaultCategoryZoom {
    private active: HTMLElement | null = null;
    private preview: HTMLDivElement | null = null;
    private mode: "hover" | "focus" | "tap" = "hover";
    private pointerType = "mouse";
    private frame: number | null = null;
    private signature = "";
    private readonly titleSizes = new WeakMap<HTMLElement, { key: string; fontSize: string; overflowWrap: string }>();
    private readonly resizeObserver: ResizeObserver | null;

    public constructor(private readonly root: HTMLElement) {
        this.resizeObserver = typeof ResizeObserver === "function" ? new ResizeObserver(() => this.sync()) : null;
        this.resizeObserver?.observe(root);
        document.fonts?.addEventListener("loadingdone", this.fontsLoaded);
        root.addEventListener("pointerover", this.over);
        root.addEventListener("pointerout", this.out);
        root.addEventListener("pointerdown", this.pointerDown);
        root.addEventListener("focusin", this.focus);
        root.addEventListener("focusout", this.blur);
        root.addEventListener("click", this.click);
        root.addEventListener("keydown", this.key);
        document.addEventListener("pointerdown", this.outside);
        document.addEventListener("keydown", this.escape);
        document.addEventListener("scroll", this.close, true);
        document.addEventListener("fullscreenchange", this.fullscreen);
        window.addEventListener("blur", this.close);
    }

    private header(target: EventTarget | null): HTMLElement | null {
        const header = target instanceof window.Element ? target.closest<HTMLElement>("[data-vault-category]") : null;
        return header && this.root.contains(header) ? header : null;
    }

    private show(header: HTMLElement, mode: typeof this.mode) {
        if (this.active !== header) this.close();
        this.active = header;
        this.mode = mode;
        if (!this.preview) {
            this.preview = document.createElement("div");
            this.preview.className = "vault-category-zoom";
            this.preview.setAttribute("aria-hidden", "true");
            // Top-layer placement also escapes clipped boards and fullscreen ancestors.
            if (typeof this.preview.showPopover === "function") this.preview.popover = "manual";
            (document.fullscreenElement ?? document.body).append(this.preview);
            if (this.preview.popover) this.preview.showPopover();
        }
        this.sync();
        this.track();
    }

    public sync() {
        this.fitTitles();
        const header = this.active;
        const preview = this.preview;
        if (!header || !preview) return;
        const title = header.querySelector<HTMLElement>(".vault-category-title");
        const rect = header.getBoundingClientRect();
        const viewport = window.visualViewport;
        const leftEdge = viewport?.offsetLeft ?? 0;
        const topEdge = viewport?.offsetTop ?? 0;
        const width = viewport?.width ?? window.innerWidth;
        const height = viewport?.height ?? window.innerHeight;
        if (!header.isConnected || !this.root.contains(header) || !title || rect.width === 0 ||
            rect.bottom <= topEdge || rect.top >= topEdge + height) {
            this.close();
            return;
        }
        if (header.getAttribute("aria-expanded") !== "true") header.setAttribute("aria-expanded", "true");
        const style = getComputedStyle(title);
        const paint = getComputedStyle(header);
        const scale = SITE_TUNING.vaultBreakers.typography.categoryZoomScale;
        const signature = [title.textContent, rect.x, rect.y, rect.width, rect.height, title.scrollHeight, title.scrollWidth, style.fontSize, style.fontFamily,
            paint.background, style.color, paint.borderColor, width, height, leftEdge, topEdge].join("|");
        if (signature === this.signature) return;
        this.signature = signature;
        preview.textContent = title.textContent;
        Object.assign(preview.style, {
            width: `${Math.min(width - 16, Math.max(320, rect.width * scale))}px`,
            fontFamily: style.fontFamily,
            fontWeight: style.fontWeight,
            fontSize: `${Number.parseFloat(style.fontSize) * scale}px`,
            lineHeight: "1.08",
            textTransform: style.textTransform,
            color: style.color,
            background: paint.background,
            borderColor: paint.borderColor,
            textShadow: style.textShadow,
            boxShadow: `${paint.boxShadow === "none" ? "" : paint.boxShadow + ", "}0 12px 28px #000b`
        });
        let bounds = preview.getBoundingClientRect();
        if (bounds.height > height - 16) {
            preview.style.width = `${width - 16}px`;
            bounds = preview.getBoundingClientRect();
        }
        preview.style.left = `${Math.max(leftEdge + 8, Math.min(rect.left + rect.width / 2 - bounds.width / 2, leftEdge + width - bounds.width - 8))}px`;
        preview.style.top = `${Math.max(topEdge + 8, Math.min(rect.top, topEdge + height - bounds.height - 8))}px`;
    }

    private fitTitles(force = false) {
        const compact = window.matchMedia?.("(max-width: 720px)").matches ?? window.innerWidth <= 720;
        this.root.querySelectorAll<HTMLElement>(".vault-category-title").forEach(title => {
            if (compact) {
                title.style.removeProperty("font-size");
                title.style.removeProperty("overflow-wrap");
                this.titleSizes.delete(title);
                return;
            }
            const width = title.clientWidth;
            if (!width) return;
            const preferred = Number.parseFloat(window.getComputedStyle(title.parentElement!).fontSize);
            const key = `${title.textContent}|${width}|${preferred}`;
            const saved = this.titleSizes.get(title);
            // Passive DOM patches can clear inline styles; restore the measured fit
            // without repeating the layout search for unchanged titles and columns.
            if (!force && saved?.key === key) {
                title.style.fontSize = saved.fontSize;
                title.style.overflowWrap = saved.overflowWrap;
                return;
            }
            const remember = () => this.titleSizes.set(title, {
                key, fontSize: title.style.fontSize, overflowWrap: title.style.overflowWrap
            });
            const { categoryCompactMinPx, categoryMaxLines } = SITE_TUNING.vaultBreakers.typography;
            // Fit whole words before allowing emergency breaks in an unusually long token.
            title.style.overflowWrap = "normal";
            const fits = (size: number) => {
                title.style.fontSize = `${size}px`;
                const lineHeight = Number.parseFloat(window.getComputedStyle(title).lineHeight);
                return title.scrollWidth <= width + 1 && title.scrollHeight <= lineHeight * categoryMaxLines + 1;
            };
            if (fits(preferred)) { remember(); return; }
            let low = Math.min(categoryCompactMinPx, preferred);
            let high = preferred;
            if (!fits(low)) {
                title.style.overflowWrap = "anywhere";
                remember();
                return;
            }
            while (high - low > 0.25) {
                const middle = (low + high) / 2;
                if (fits(middle)) low = middle;
                else high = middle;
            }
            title.style.fontSize = `${low}px`;
            remember();
        });
    }

    private readonly fontsLoaded = () => { this.fitTitles(true); this.sync(); };

    private track() {
        if (this.frame !== null || !this.active) return;
        this.frame = requestAnimationFrame(() => {
            this.frame = null;
            this.sync();
            this.track();
        });
    }

    private over = (event: PointerEvent) => {
        const header = this.header(event.target);
        if (header && event.pointerType !== "touch") this.show(header, "hover");
    };
    private out = (event: PointerEvent) => {
        if (this.mode === "hover" && this.active && this.header(event.relatedTarget) !== this.active) this.close();
    };
    private pointerDown = (event: PointerEvent) => { this.pointerType = event.pointerType; };
    private focus = (event: FocusEvent) => {
        const header = this.header(event.target);
        if (header?.matches(":focus-visible")) this.show(header, "focus");
    };
    private blur = (event: FocusEvent) => {
        if (this.active && this.header(event.relatedTarget) !== this.active) this.close();
    };
    private click = (event: MouseEvent) => {
        const header = this.header(event.target);
        if (header && (this.pointerType === "touch" || event.detail === 0)) this.toggle(header);
    };
    private key = (event: KeyboardEvent) => {
        const header = this.header(event.target);
        if (header && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            this.toggle(header);
        }
    };
    private toggle(header: HTMLElement) {
        if (this.active === header) this.close();
        else this.show(header, "tap");
    }
    private outside = (event: PointerEvent) => { if (!this.header(event.target)) this.close(); };
    private fullscreen = () => {
        if (!this.active) return;
        const header = this.active;
        const mode = this.mode;
        this.close();
        this.show(header, mode);
    };
    private escape = (event: KeyboardEvent) => { if (event.key === "Escape") this.close(); };
    private close = () => {
        this.active?.setAttribute("aria-expanded", "false");
        this.active = null;
        this.preview?.remove();
        this.preview = null;
        this.signature = "";
        if (this.frame !== null) cancelAnimationFrame(this.frame);
        this.frame = null;
    };

    public destroy() {
        this.resizeObserver?.disconnect();
        document.fonts?.removeEventListener("loadingdone", this.fontsLoaded);
        this.close();
        this.root.removeEventListener("pointerover", this.over);
        this.root.removeEventListener("pointerout", this.out);
        this.root.removeEventListener("pointerdown", this.pointerDown);
        this.root.removeEventListener("focusin", this.focus);
        this.root.removeEventListener("focusout", this.blur);
        this.root.removeEventListener("click", this.click);
        this.root.removeEventListener("keydown", this.key);
        document.removeEventListener("pointerdown", this.outside);
        document.removeEventListener("keydown", this.escape);
        document.removeEventListener("scroll", this.close, true);
        document.removeEventListener("fullscreenchange", this.fullscreen);
        window.removeEventListener("blur", this.close);
    }
}
